import * as Yup from 'yup'
import {
  finalizeWizardDooraCard,
  loadApplication,
  saveApplication,
  sendWizardOtp,
  verifyWizardOtp,
  sendCoApplicantInviteOtp,
  verifyCoApplicantInvite
} from '../../api'
import {
  IApplicationFormData,
  IWizardIncome,
  normalizeApplicationFormData,
} from '../../models/application-form'
import {
  IStepConfig,
  IWizardStepActionContext,
  IWizardStepActionResult,
} from '../../models/step-config'
import CompanyTrustStep from '../steps/CompanyTrustStep'
import IncomeStep from '../steps/IncomeStep'
import PetsVehiclesStep from '../steps/PetsVehiclesStep'
import PreferencesStep from '../steps/PreferencesStep'
import PrivacyStep from '../steps/PrivacyStep'
import RentalHistoryStep from '../steps/RentalHistoryStep'
import Start from '../steps/Start'
import UploadsStep from '../steps/UploadsStep'
import {requiredUploadSlotTypes, UPLOAD_SLOT_TYPES} from '../steps/sections/uploadSlots'
import VerifyOtp from '../steps/VerifyOtp'
import ApplicationTypeStep from '../steps/ApplicationTypeStep'
import ProfileStep from '../steps/ProfileStep'
import HouseHoldStep from '../steps/HouseHoldStep'
import EmploymentStep from '../steps/EmploymentStep'
import ReviewStep from '../steps/ReviewStep'
import OtherOccupantsStep from '../steps/OtherOccupantsStep'

const AU_MOBILE_REGEX = /^04\d{8}$/
const PHONE_REGEX = /^[0-9+\-\s()]{8,20}$/
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const normalizeMobile = (mobileNumber?: string): string => {
  if (!mobileNumber) {
    return ''
  }

  return mobileNumber.replace(/\D/g, '')
}

const isValidAustralianMobile = (mobileNumber?: string): boolean => {
  return AU_MOBILE_REGEX.test(normalizeMobile(mobileNumber))
}

const isValidEmailAddress = (email?: string): boolean => EMAIL_REGEX.test((email ?? '').trim())
const isCoApplicantWizard = (data?: Partial<IApplicationFormData>): boolean =>
    (data?.renterRole ?? '').toLowerCase() === 'co-applicant'

const isInviteWizard = (data?: Partial<IApplicationFormData>): boolean =>
    isCoApplicantWizard(data)

const hasVerifiedDraftContext = (data: Partial<IApplicationFormData>): boolean =>
    data.isMobileVerified === true && !!data.sessionId?.trim() && Number(data.applicationId) > 0

const hasSelectedIncomeSource = (income?: IWizardIncome): boolean =>
  income?.hasEmploymentIncome === true || income?.hasSelfEmploymentIncome === true

const hasEmploymentIncomeSource = (income?: IWizardIncome): boolean =>
  income?.hasEmploymentIncome === true

const hasBenefitIncomeSource = (income?: IWizardIncome): boolean =>
  income?.hasPension === true ||
  income?.isCentreLink === true ||
  income?.hasNdis === true ||
  income?.hasStudentSupport === true

const hasRequiredValue = (value: unknown): boolean => {
  if (value === undefined || value === null) {
    return false
  }

  if (typeof value === 'number') {
    return Number.isFinite(value)
  }

  return typeof value !== 'string' || value.trim().length > 0
}

const toBooleanChoice = (value: unknown): boolean | undefined => {
  if (value === true || value === false) {
    return value
  }

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    if (normalized === 'true') {
      return true
    }

    if (normalized === 'false') {
      return false
    }
  }

  return undefined
}

const hasValidStudentIdentifier = (value?: string): boolean => {
  const usi = value?.trim() ?? ''
  return usi.length === 10 && /^[A-Z0-9]+$/.test(usi)
}

const hasCompleteGovernmentBenefit = (data?: Partial<IApplicationFormData>): boolean => {
  const income = data?.income
  const employment = data?.employment

  return (
    (income?.hasPension === true &&
      hasRequiredValue(income.pensionAmount) &&
      hasRequiredValue(income.pensionFrequency)) ||
    (income?.isCentreLink === true &&
      hasRequiredValue(income.centreAmount) &&
      hasRequiredValue(income.centreLinkFrequency)) ||
    (income?.hasNdis === true &&
      hasRequiredValue(income.ndisAmount) &&
      hasRequiredValue(income.ndisFrequency)) ||
    (income?.hasStudentSupport === true &&
      hasRequiredValue(income.studentSupportAmount) &&
      hasRequiredValue(income.studentSupportFrequency) &&
      hasValidStudentIdentifier(employment?.uniqueStudentIdentifier))
  )
}

const canContinueIncomeStep = (data?: Partial<IApplicationFormData>): boolean => {
  if (!data) {
    return false
  }

  return hasSelectedIncomeSource(data.income) || hasCompleteGovernmentBenefit(data)
}

const getIncomeStepRequirementMessage = (): string =>
  'Select at least one income source to continue.'

const isValidABN = (abn: string) => {
  if (!/^\d{11}$/.test(abn)) return false

  const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19]
  const digits = abn.split('').map(Number)

  digits[0] -= 1

  const sum = digits.reduce((acc, digit, i) => acc + digit * weights[i], 0)

  return sum % 89 === 0
}

const isValidACN = (acn: string) => {
  if (!/^\d{9}$/.test(acn)) return false

  const weights = [8, 7, 6, 5, 4, 3, 2, 1]
  const digits = acn.split('').map(Number)

  const checkDigit = digits.pop()!
  const sum = digits.reduce((acc, digit, i) => acc + digit * weights[i], 0)

  const remainder = sum % 10
  const calculated = (10 - remainder) % 10

  return calculated === checkDigit
}

const calculateLockedUntilUtc = (
  retryAfterSeconds?: number,
  lockedUntilUtc?: string
): string | undefined => {
  if (lockedUntilUtc) {
    return lockedUntilUtc
  }

  if (!retryAfterSeconds || retryAfterSeconds <= 0) {
    return undefined
  }

  return new Date(Date.now() + retryAfterSeconds * 1000).toISOString()
}

const getUploadedDocumentTypes = (
  documents?: Array<{type?: string; filename?: string}>
): Set<string> =>
  new Set(
    (documents ?? []).filter((item) => !!item?.filename?.trim()).map((item) => item?.type ?? '')
  )

const saveDraftStep = async (
  data: IApplicationFormData,
  stepKey: string,
  isAutosave = false,
  updateRetentionPreference = false
): Promise<IWizardStepActionResult> => {
  if (!data.isMobileVerified) {
    return {
      success: true,
    }
  }

  if (!data.applicationId) {
    return {
      success: false,
      error: 'Application is not initialized. Please verify your mobile again.',
    }
  }

  try {
    const result = await saveApplication(data, {
      isAutosave,
      stepKey,
      updateRetentionPreference,
    })

    return {
      success: true,
      updatedValues: {
        applicationId: result.applicationId,
        ...(!isAutosave ? {stepKey} : {}),
        status: result.status === 'submitted' ? 'submitted' : 'draft',
        lastSavedAtUtc: result.savedAtUtc,
        ...(updateRetentionPreference
          ? {retentionSelectedAtUtc: result.savedAtUtc, retentionPreferenceSelection: undefined}
          : {}),
      },
      ...(stepKey === 'pets-vehicles'
        ? {
            persistedPets: result.pets,
            persistedVehicles: result.vehicles,
          }
        : {}),
      ...(stepKey === 'other-occupants'
        ? {persistedOtherOccupants: result.otherOccupants}
        : {}),
    }
  } catch (error) {
    return {
      success: false,
      error,
    }
  }
}

const saveNext = (stepKey: string) => async (data: IApplicationFormData) =>
  saveDraftStep(data, stepKey, false)
const hasRetentionSelection = (data: IApplicationFormData): boolean =>
  data.renterRole === 'primary' && data.retentionPreferenceSelection !== undefined
const privacyStepNext = (data: IApplicationFormData) =>
  saveDraftStep(
    data,
    'review',
    false,
    data.renterRole === 'primary' && (hasRetentionSelection(data) || !data.retentionSelectedAtUtc)
  )
const savePrev =
  (stepKey: string) => async (data: IApplicationFormData, context?: IWizardStepActionContext) => {
    if (!context?.resumeStepKey || context.resumeStepKey === stepKey) {
      return saveDraftStep(data, stepKey, false, stepKey === 'privacy' && hasRetentionSelection(data))
    }

    const autosaveResult = await saveDraftStep(
      data, stepKey, true, stepKey === 'privacy' && hasRetentionSelection(data)
    )
    if (!autosaveResult.success) {
      return autosaveResult
    }

    const resumeResult = await saveDraftStep(data, context.resumeStepKey, false)
    return {
      ...autosaveResult,
      ...resumeResult,
      updatedValues: {...autosaveResult.updatedValues, ...resumeResult.updatedValues},
    }
  }
const saveAuto = (stepKey: string) => async (data: IApplicationFormData) =>
  saveDraftStep(data, stepKey, true, stepKey === 'privacy' && hasRetentionSelection(data))

const startStepNext = async (data: IApplicationFormData): Promise<IWizardStepActionResult> => {
  const mobileNumber = normalizeMobile(data.mobileNumber)

  if (!AU_MOBILE_REGEX.test(mobileNumber)) {
    return {
      success: false,
      error: 'Please enter a valid Australian mobile number.',
    }
  }

  if (isInviteWizard(data)) {
    
    const inviteId = data.coApplicantInviteId?.trim();
      if (!inviteId) {
          return {
              success: false,
              error: 'Invite reference is missing. Please open the invitation link again.'
          }
      }

      try {
          const result = await sendCoApplicantInviteOtp({inviteId, mobileNumber})

          if (!result.success) {
              return {
                  success: false,
                  error: result.message ?? 'Unable to send OTP code.',
                  updatedValues: {
                      otpRetryAfterSeconds: result.retryAfterSeconds,
                      otpRequiresNewCode: result.requiresNewCode,
                      otpLockedUntilUtc: result.isLockedOut ? calculateLockedUntilUtc(result.retryAfterSeconds, result.lockedUntilUtc) : undefined
                  }
              }
          }

          return {
              success: true,
              updatedValues: {
                  renterRole: data.renterRole,
                  coApplicantInviteId: inviteId,
                  primaryApplicantName: data.primaryApplicantName,
                  mobileNumber,
                  firstName: data.firstName,
                  lastName: data.lastName,
                  fullName: data.fullName,
                  termsAccepted: data.termsAccepted,
                  consentSharingAccepted: data.consentSharingAccepted,
                  otpRetryAfterSeconds: result.retryAfterSeconds ?? 60,
                  otpRequiresNewCode: false,
                  otpLockedUntilUtc: undefined
              }
          }
      }
      catch (error) {
          return {
              success: false,
              error
          }
      }
  }

  try {
    const result = await sendWizardOtp({
      mobileNumber,
    })

    if (!result.sent) {
      return {
        success: false,
        error: result.message ?? 'Unable to send OTP code.',
        updatedValues: {
          otpRetryAfterSeconds: result.retryAfterSeconds,
          otpRequiresNewCode: false,
          otpLockedUntilUtc: result.isLockedOut
            ? calculateLockedUntilUtc(result.retryAfterSeconds, result.lockedUntilUtc)
            : undefined,
        },
      }
    }

    return {
      success: true,
      updatedValues: {
        mobileNumber,
        otpRetryAfterSeconds: result.retryAfterSeconds ?? 60,
        otpRequiresNewCode: false,
        otpLockedUntilUtc: undefined,
      },
    }
  } catch (error) {
    return {
      success: false,
      error,
    }
  }
}

const verifyInviteStepNext = async (
    data: IApplicationFormData,
    mobileNumber: string,
    otpCode: string
): Promise<IWizardStepActionResult> => {
    const inviteId = data.coApplicantInviteId?.trim();
    if (!inviteId) {
        return {
            success: false,
            error: 'Invite reference is missing. Please open the invitation link again.'
        }
    }

    try {
        const verifyResult = await verifyCoApplicantInvite({inviteId, mobileNumber, otpCode})

        if (!verifyResult.verified) {
            return {
                success: false,
                error: verifyResult.failureCode ?? 'Unable to verify OTP.',
                updatedValues: {
                    otpRetryAfterSeconds: verifyResult.retryAfterSeconds,
                    otpRequiresNewCode: verifyResult.requiresNewCode ?? false,
                    otpLockedUntilUtc: verifyResult.isLockedOut ? calculateLockedUntilUtc(verifyResult.retryAfterSeconds, verifyResult.lockedUntilUtc) : undefined
                }
            }
        }

        if (verifyResult.sessionId && verifyResult.nextStepPath === 'household-warning') {
            return {
                success: true,
                redirectTo: `/application-wizard/profile?sessionId=${encodeURIComponent(verifyResult.sessionId)}`,
            }
        }

        if (!verifyResult.sessionId || !verifyResult.applicationId) {
            return {
                success: false,
                error: 'Verified session id was not returned.'
            }
        }

        const loadedDraft = await loadApplication(verifyResult.sessionId);

        return {
            success: true,
            updatedValues: {
                ...loadedDraft,
                coApplicantInviteId: inviteId,
                renterRole: data.renterRole,
                primaryApplicantName: data.primaryApplicantName,
                sessionId: verifyResult.sessionId,
                applicationId: verifyResult.applicationId,
                termsAccepted: data.termsAccepted,
                consentSharingAccepted: data.consentSharingAccepted,
                retentionPreference: loadedDraft.retentionPreference,
                retentionPreferenceText: loadedDraft.retentionPreferenceText,
                otpCodeValid: true,
                isMobileVerified: true,
                mobileVerifiedAtUtc: verifyResult.invite?.verifiedAtUtc ?? loadedDraft.mobileVerifiedAtUtc,
                otpRetryAfterSeconds: undefined,
                otpLockedUntilUtc: undefined,
                otpRequiresNewCode: false
            }
        }
    } catch (error) {
        console.error('[wizard-coapp] Co-applicant invite verification failed', {
            error: error instanceof Error ? error.message : String(error)
        })

        return {
            success: false,
            error
        }
    }
}

const verifyMobileStepNext = async (
  data: IApplicationFormData
): Promise<IWizardStepActionResult> => {
  const mobileNumber = normalizeMobile(data.mobileNumber)
  const otpCode = (data.otpCode ?? '').trim()
  let stage = 'verify-otp'

  if (!mobileNumber) {
    return {
      success: false,
      error: 'Mobile number is required.',
    }
  }

  if(isInviteWizard(data)){
      return verifyInviteStepNext(data, mobileNumber, otpCode);
  }

  const wizardAction = data.action === 'resume' ? 'resume' : 'new'

  try {
    const verifyResult = await verifyWizardOtp({
      mobileNumber,
      otpCode,
      action: wizardAction,
      fullName: data.fullName,
      firstName: data.firstName,
      lastName: data.lastName,
    })
    if (!verifyResult.verified) {
      return {
        success: false,
        error: verifyResult.failureCode ?? 'Unable to verify OTP.',
        updatedValues: {
          otpRetryAfterSeconds: verifyResult.retryAfterSeconds,
          otpRequiresNewCode: verifyResult.requiresNewCode ?? false,
          otpLockedUntilUtc: verifyResult.isLockedOut
            ? calculateLockedUntilUtc(verifyResult.retryAfterSeconds, verifyResult.lockedUntilUtc)
            : undefined,
        },
      }
    }

    if (!verifyResult.sessionId) {
      return {
        success: false,
        error: 'Verified session id was not returned.',
      }
    }

    if (wizardAction === 'resume') {
      stage = 'load-application'
      const loadedDraft = await loadApplication(verifyResult.sessionId)

      return {
        success: true,
        updatedValues: {
          ...loadedDraft,
          sessionId: verifyResult.sessionId,
          action: undefined,
          otpCodeValid: true,
          isMobileVerified: true,
          mobileVerifiedAtUtc: verifyResult.verifiedAtUtc ?? loadedDraft.mobileVerifiedAtUtc,
          otpRetryAfterSeconds: undefined,
          otpLockedUntilUtc: undefined,
          otpRequiresNewCode: false,
        },
      }
    }

    stage = 'initialize-new-application'
    const verifiedState: Partial<IApplicationFormData> = {
      action: 'new',
      sessionId: verifyResult.sessionId,
      otpCodeValid: true,
      isMobileVerified: true,
      mobileVerifiedAtUtc: verifyResult.verifiedAtUtc,
    }

    const draftToSave = normalizeApplicationFormData({
      ...data,
      ...verifiedState,
      mobileNumber,
      status: 'draft',
      applicationType: undefined,
      companyTrustAbnAcn: undefined,
      companyTrustDirectorId: undefined,
      companyTrustLetterOfAuthorityDocumentId: undefined,
      coApplicants: [],
    })

    const saveResult = await saveApplication(draftToSave, {
      stepKey: 'verify-mobile',
    })

    return {
      success: true,
      updatedValues: {
        ...verifiedState,
        applicationType: undefined,
        companyTrustAbnAcn: undefined,
        companyTrustDirectorId: undefined,
        companyTrustLetterOfAuthorityDocumentId: undefined,
        coApplicants: [],
        applicationId: saveResult.applicationId,
        stepKey: 'verify-mobile',
        status: saveResult.status === 'submitted' ? 'submitted' : 'draft',
        lastSavedAtUtc: saveResult.savedAtUtc,
        otpRetryAfterSeconds: undefined,
        otpLockedUntilUtc: undefined,
        otpRequiresNewCode: false,
      },
    }
  } catch (error) {
    console.error('[wizard-resume] Wizard verification flow failed', {
      stage,
      action: wizardAction,
      error: error instanceof Error ? error.message : String(error),
    })
    return {
      success: false,
      error,
    }
  }
}

const reviewStepFinalize = async (data: IApplicationFormData): Promise<IWizardStepActionResult> => {
  const saveResult = await saveDraftStep(data, 'review', false)
  if (!saveResult.success) {
    return saveResult
  }

  if (!data.sessionId) {
    return {
      success: false,
      error: 'Session expired. Please verify your mobile again.',
    }
  }

  try {
    const result = await finalizeWizardDooraCard({
      sessionId: data.sessionId,
    })

    if (result.isBlocked) {
      return {success: false, error: result.message ?? 'Your saved Doora Card is not ready.'}
    }

    return {
      success: true,
    }
  } catch (error) {
    return {
      success: false,
      error,
    }
  }
}

const coApplicantSchema = Yup.object({
  inviteId: Yup.string().nullable(),
  fullName: Yup.string().required('Co-applicant name is required.'),
  mobileNumber: Yup.string()
    .required('Co-applicant mobile number is required.')
    .test('au-mobile', 'Enter a valid Australian mobile number.', (value) =>
      isValidAustralianMobile(value)
    ),
  //email: Yup.string()
  //  .required('Co-applicant email is required.')
  //  .test('email-format', 'Enter a valid email address.', (value) => isValidEmailAddress(value)),
  role: Yup.string().required('Role is required.'),
  status: Yup.string().oneOf(['pending', 'verified', 'accepted', 'revoked', 'declined']).notRequired(),
  invitedAtUtc: Yup.string().notRequired(),
  inviteExpiresAtUtc: Yup.string().notRequired(),
  verifiedAtUtc: Yup.string().nullable().notRequired(),
  revokedAtUtc: Yup.string().nullable().notRequired(),
  declinedAtUtc:  Yup.string().nullable().notRequired(),
  declineReason:  Yup.string().nullable().notRequired()
})

const optionalAmountSchema = () =>
  Yup.number()
    .nullable()
    .transform((value, originalValue) => {
      if (originalValue === '' || originalValue === null || originalValue === undefined) {
        return null
      }

      return isNaN(value) ? null : value
    })

const optionalTrimmedStringSchema = () =>
  Yup.string()
    .nullable()
    .transform((value) => (typeof value === 'string' ? value.trim() : value))

export const stepConfig: IStepConfig[] = [
  {
    id: 1,
    key: 'start',
    path: 'create-account',
    label: 'Create Account',
    component: Start,
    showProgressBar: true,
    title: 'Create Account',
    nextButtonText: 'Send code',
    schema: Yup.object({
      fullName: Yup.string().required('Enter your full name.'),
      mobileNumber: Yup.string()
        .required('Mobile number is required.')
        .test('au-mobile', 'Enter a valid Australian mobile number.', (value) =>
          isValidAustralianMobile(value)
        ),
      termsAccepted: Yup.boolean().oneOf([true], 'Please accept the Terms and Privacy Policy.'),
      consentSharingAccepted: Yup.boolean().oneOf([true], 'Please tick to consent.'),
      action: Yup.string()
        .oneOf(['resume', 'new'])
        .when('existingApplicationId', {
          is: (value: number | undefined) => !!value,
          then: (schema) => schema.required('Choose Resume or Start New.'),
          otherwise: (schema) => schema.notRequired(),
        }),
    }),
    condition: (data) => !isInviteWizard(data) || !hasVerifiedDraftContext(data),
    onNext: startStepNext,
  },
  {
    id: 2,
    key: 'verify-mobile',
    path: 'verify-mobile',
    label: 'Verify',
    component: VerifyOtp,
    showProgressBar: true,
    title: 'Verify Code',
    nextButtonText: 'Continue',
    schema: Yup.object({
      otpCode: Yup.string()
        .required('Enter the 6-digit OTP code.')
        .length(6, 'Enter the 6-digit OTP code.'),
    }),
    condition: (data) => !isInviteWizard(data) || !hasVerifiedDraftContext(data),
    onNext: verifyMobileStepNext,
  },
  {
    id: 3,
    key: 'application-type',
    path: 'application-type',
    label: 'Type',
    title: 'Application Type',
    showProgressBar: true,
    component: ApplicationTypeStep,
    nextButtonText: 'Continue',
    isSaveEnabled: true,
    schema: Yup.object({
      applicationType: Yup.string()
        .oneOf(['Individual', 'Couple', 'Housemates', 'Family', 'CompanyTrust'])
        .required('Application type is required.'),
    }),
    condition: (data) => !isInviteWizard(data),
    onNext: saveNext('application-type'),
    onPrev: savePrev('application-type'),
    onAutoSave: saveAuto('application-type'),
  },
  {
    id: 4,
    key: 'company-trust',
    path: 'company-trust',
    label: 'Company',
    title: 'Company / Trust',
    showProgressBar: true,
    component: CompanyTrustStep,
    nextButtonText: 'Save & Continue',
    isSaveEnabled: true,
    condition: (data) => !isInviteWizard(data) && data.applicationType === 'CompanyTrust',
    schema: Yup.object({
      companyTrustAbnAcn: Yup.string()
        .required('ABN or ACN is required.')
        .test('abn-acn-validation', 'Enter a valid ABN (11 digits) or ACN (9 digits).', (value) => {
          if (!value) return false

          const cleaned = (value ?? '').replace(/\s/g, '')

          return isValidABN(cleaned) || isValidACN(cleaned)
        }),

      companyTrustDirectorId: Yup.string().required('Director ID is required.'),
    }),
    onNext: saveNext('company-trust'),
    onPrev: savePrev('company-trust'),
    onAutoSave: saveAuto('company-trust'),
  },
  {
    id: 5,
    key: 'household',
    path: 'household',
    label: 'Household',
    title: 'Household',
    showProgressBar: true,
    component: HouseHoldStep,
    nextButtonText: 'Continue',
    isSaveEnabled: true,
    condition: (data) =>
      !isInviteWizard(data) &&
      (data.applicationType === 'Couple' ||
        data.applicationType === 'Housemates' ||
        data.applicationType === 'Family'),
    schema: Yup.object({
      coApplicants: Yup.array()
        .of(coApplicantSchema)
        .min(1, 'At least one co-applicant is required.')
        .test('all-verified', 'All co-applicants must be verified.', function (value: any) {
          const BlockNotVerified = (this.options.context as {BlockNotVerified?: boolean})
            ?.BlockNotVerified

          if (!BlockNotVerified) return true

          return value?.every((c: any) => c.status !== 'pending')
        }),
    }),
    onNext: saveNext('household'),
    onPrev: savePrev('household'),
    onAutoSave: saveAuto('household'),
  },
  {
    id: 6,
    key: 'profile',
    path: 'profile',
    label: 'Profile',
    title: 'The Basics',
    showProgressBar: true,
    component: ProfileStep,
    nextButtonText: 'Continue',
    isSaveEnabled: true,
    schema: Yup.object({
      dateOfBirth: Yup.date()
        .nullable()
        .transform((value, originalValue) => {
          if (!originalValue) return null

          const parsed = new Date(originalValue)
          return isNaN(parsed.getTime()) ? null : parsed
        })
        .typeError('Enter your date of birth.')
        .required('Enter your date of birth.')
        .min(new Date(1900, 0, 1), 'Date of birth must be after January 1, 1900'),
      australianStatus: Yup.string().nullable().required('Pick one.'),
      email: Yup.string()
        .nullable()
        .required('Enter a valid email address.')
        .email('Enter a valid email address.'),
      emergencyContactName: Yup.string().nullable().required('Enter their full name.'),
      emergencyContactPhone: Yup.string()
        .nullable()
        .required('Enter a valid Australian mobile number.')
        .test('au-mobile', 'Enter a valid Australian mobile number.', (value) =>
          isValidAustralianMobile(value ?? '')
        ),
      emergencyContactRelationship: Yup.string().nullable().required('Pick a relationship.'),
    }),
    //condition: (data) => !isCoApplicantWizard(data),
    onNext: saveNext('profile'),
    onPrev: savePrev('profile'),
    onAutoSave: saveAuto('profile'),
  },
  {
    id: 7,
    key: 'income',
    path: 'income',
    label: 'Income',
    title: 'Income',
    footerText: 'Pick at least one income source.',
    showProgressBar: true,
    component: IncomeStep,
    nextButtonText: 'Continue',
    isSaveEnabled: true,
    schema: Yup.object({
      income: Yup.object({
        hasEmploymentIncome: Yup.boolean(),
        hasSelfEmploymentIncome: Yup.boolean(),
        income: optionalAmountSchema().when('hasEmploymentIncome', {
          is: true,
          then: (schema) => schema.required('Income is required.'),
          otherwise: (schema) => schema.notRequired(),
        }),
        incomeFrequency: optionalTrimmedStringSchema().when('hasEmploymentIncome', {
          is: true,
          then: (schema) => schema.required('Income frequency is required.'),
          otherwise: (schema) => schema.notRequired(),
        }),
        selfEmploymentIncome: optionalAmountSchema().when('hasSelfEmploymentIncome', {
          is: true,
          then: (schema) => schema.required('Self-employment income is required.'),
          otherwise: (schema) => schema.notRequired(),
        }),
        selfEmploymentIncomeFrequency: optionalTrimmedStringSchema().when(
          'hasSelfEmploymentIncome',
          {
            is: true,
            then: (schema) => schema.required('Self-employment income frequency is required.'),
            otherwise: (schema) => schema.notRequired(),
          }
        ),
        hasPension: Yup.boolean(),
        pensionAmount: optionalAmountSchema().when('hasPension', {
          is: true,
          then: (schema) => schema.required('Pension amount is required.'),
          otherwise: (schema) => schema.notRequired(),
        }),
        pensionFrequency: optionalTrimmedStringSchema().when('hasPension', {
          is: true,
          then: (schema) => schema.required('Pension frequency is required.'),
          otherwise: (schema) => schema.notRequired(),
        }),
        isCentreLink: Yup.boolean(),
        centreAmount: optionalAmountSchema().when('isCentreLink', {
          is: true,
          then: (schema) => schema.required('Centrelink amount is required.'),
          otherwise: (schema) => schema.notRequired(),
        }),
        centreLinkFrequency: optionalTrimmedStringSchema().when('isCentreLink', {
          is: true,
          then: (schema) => schema.required('Centrelink frequency is required.'),
          otherwise: (schema) => schema.notRequired(),
        }),
        hasNdis: Yup.boolean(),
        ndisAmount: optionalAmountSchema().when('hasNdis', {
          is: true,
          then: (schema) => schema.required('NDIS amount is required.'),
          otherwise: (schema) => schema.notRequired(),
        }),
        ndisFrequency: optionalTrimmedStringSchema().when('hasNdis', {
          is: true,
          then: (schema) => schema.required('NDIS frequency is required.'),
          otherwise: (schema) => schema.notRequired(),
        }),
        hasStudentSupport: Yup.boolean(),
        studentSupportAmount: optionalAmountSchema().when('hasStudentSupport', {
          is: true,
          then: (schema) => schema.required('Student support amount is required.'),
          otherwise: (schema) => schema.notRequired(),
        }),
        studentSupportFrequency: optionalTrimmedStringSchema().when('hasStudentSupport', {
          is: true,
          then: (schema) => schema.required('Student support frequency is required.'),
          otherwise: (schema) => schema.notRequired(),
        }),
      }),
      employment: Yup.object({
        businessName: optionalTrimmedStringSchema(),
        abn: optionalTrimmedStringSchema(),
        businessStructure: optionalTrimmedStringSchema(),
        role: optionalTrimmedStringSchema(),
        uniqueStudentIdentifier: optionalTrimmedStringSchema(),
      }),
    })
      .test(
        'income-step-can-continue',
        'Select at least one income source to continue.',
        function (value) {
          const data = (value ?? {}) as Partial<IApplicationFormData>
          const message = getIncomeStepRequirementMessage()

          return (
            canContinueIncomeStep(data) ||
            this.createError({
              path: 'income',
              message,
            })
          )
        }
      )
      .test('self-employment-business-name', 'Business name is required.', function (value) {
        const data = (value ?? {}) as Partial<IApplicationFormData>
        if (
          data.income?.hasSelfEmploymentIncome !== true ||
          data.employment?.businessName?.trim()
        ) {
          return true
        }

        return this.createError({
          path: 'employment.businessName',
          message: 'Business name is required.',
        })
      })
      .test('self-employment-abn', 'ABN is required.', function (value) {
        const data = (value ?? {}) as Partial<IApplicationFormData>
        if (data.income?.hasSelfEmploymentIncome !== true || data.employment?.abn?.trim()) {
          return true
        }

        return this.createError({path: 'employment.abn', message: 'ABN is required.'})
      })
      .test(
        'self-employment-business-structure',
        'Business structure is required.',
        function (value) {
          const data = (value ?? {}) as Partial<IApplicationFormData>
          if (
            data.income?.hasSelfEmploymentIncome !== true ||
            data.employment?.businessStructure?.trim()
          ) {
            return true
          }

          return this.createError({
            path: 'employment.businessStructure',
            message: 'Business structure is required.',
          })
        }
      )
      .test('self-employment-role', 'Role is required.', function (value) {
        const data = (value ?? {}) as Partial<IApplicationFormData>
        if (data.income?.hasSelfEmploymentIncome !== true || data.employment?.role?.trim()) {
          return true
        }

        return this.createError({path: 'employment.role', message: 'Role is required.'})
      })
      .test('student-support-usi', 'Unique student identifier is required.', function (value) {
        const data = (value ?? {}) as Partial<IApplicationFormData>
        if (data.income?.hasStudentSupport !== true) {
          return true
        }

        const usi = data.employment?.uniqueStudentIdentifier?.trim() ?? ''
        if (usi.length === 10 && /^[A-Z0-9]+$/.test(usi)) {
          return true
        }

        return this.createError({
          path: 'employment.uniqueStudentIdentifier',
          message:
            usi.length === 0
              ? 'Unique student identifier is required.'
              : usi.length !== 10
                ? 'Unique student identifier must be exactly 10 characters.'
                : 'Unique student identifier can only contain A-Z and 0-9.',
        })
      }),
    onNext: saveNext('income'),
    onPrev: savePrev('income'),
    onAutoSave: saveAuto('income'),
  },
  {
    id: 8,
    key: 'employment',
    path: 'employment',
    label: 'Employment',
    title: 'Work',
    showProgressBar: true,
    component: EmploymentStep,
    nextButtonText: 'Continue',
    isSaveEnabled: true,
    schema: Yup.object({
      employment: Yup.object({
        employmentType: Yup.string().required('Employment type is required.'),
        companyName: Yup.string().nullable().required('Enter your employer.'),

        /*startDate: Yup.date()
              .nullable()
              .transform((value, originalValue) => {
                if (!originalValue) return null

                const parsed = new Date(originalValue)
                return isNaN(parsed.getTime()) ? null : parsed
              })
              .typeError('Invalid date format')
              .when('employmentType', {
                is: (val: string) => val !== 'self-employed' && val !== 'unemployed',
                then: (schema) =>
                  schema
                    .required('Start date is required.')
                    .min(new Date(1900, 0, 1), 'Start Date must be after January 1, 1900'),
                otherwise: (schema) => schema.notRequired(),
              }),*/
        additionalReferenceName: Yup.string().nullable().required('Enter their full name.'),
        additionalReferencePhone: Yup.string()
          .nullable()
          .required('Enter a valid Australian mobile number.')
          .test(
            'is-valid-australian-mobile',
            'Enter a valid Australian mobile number.',
            (value) => !value || isValidAustralianMobile(value)
          ),
        additionalReferenceEmail: Yup.string()
          .nullable()
          .required('Enter a valid email address.')
          .email('Enter a valid email address.'),
      }),
    }),
    condition: (data) => hasEmploymentIncomeSource(data.income),
    onNext: saveNext('employment'),
    onPrev: savePrev('employment'),
    onAutoSave: saveAuto('employment'),
  },
  {
    id: 9,
    key: 'preferences',
    path: 'preferences',
    label: 'Preferences',
    title: "What You're Looking For",
    showProgressBar: true,
    component: PreferencesStep,
    nextButtonText: 'Continue',
    isSaveEnabled: true,
    schema: Yup.object({
      moveInAsap: Yup.boolean().required('Please choose your preferred move-in timing.'),
      moveInPreferredDate: Yup.date()
        .transform((value, originalValue) => (originalValue === '' ? null : value))
        .when('moveInAsap', {
          is: false,
          then: (schema) =>
            schema
              .nullable()
              .transform((value, originalValue) => {
                if (!originalValue) return null

                const parsed = new Date(originalValue)
                return isNaN(parsed.getTime()) ? null : parsed
              })
              .typeError('Invalid date format')
              .required('Please choose your preferred move-in date.')
              .min(new Date(1900, 0, 1), 'Move-in date must be after January 1, 1900'),
          otherwise: (schema) => schema.nullable().notRequired(),
        }),
      preferredLeaseTerm: Yup.string().required('Preferred lease term is required.'),
      rentWeeklyBudgetMin: Yup.number()
        .typeError('Minimum budget must be a number.')
        .required('Minimum budget is required.')
        .min(50, 'Minimum budget must be $50.'),
      rentWeeklyBudgetMax: Yup.number()
        .typeError('Maximum budget must be a number.')
        .required('Maximum budget is required.')
        .moreThan(
          Yup.ref('rentWeeklyBudgetMin'),
          'Maximum budget must be greater than minimum budget.'
        )
        .max(5000, 'Maximum budget must be $5000.'),
    }),
    condition: (data) => !isInviteWizard(data),
    onNext: saveNext('preferences'),
    onPrev: savePrev('preferences'),
    onAutoSave: saveAuto('preferences'),
  },
  {
    id: 10,
    key: 'rental-history',
    path: 'rental-history',
    label: 'History',
    title: 'Rental History',
    showProgressBar: true,
    component: RentalHistoryStep,
    nextButtonText: 'Continue',
    isSaveEnabled: true,
    schema: Yup.object({
      previousRentalHistory: Yup.mixed().test(
        'rental-history-selected',
        'Please confirm whether you have rental history.',
        (value) => toBooleanChoice(value) !== undefined
      ),
      previousPropertyManager: Yup.string().when('previousRentalHistory', {
        is: (value: unknown) => toBooleanChoice(value) === true,
        then: (schema) => schema.required('Enter their name.'),
        otherwise: (schema) => schema.notRequired(),
      }),

      previousPropertyManagerPhone: Yup.string()
        .transform((value) => (value === '' ? null : value))
        .when('previousRentalHistory', {
          is: (value: unknown) => toBooleanChoice(value) === true,
          then: (schema) =>
            schema
              .nullable()
              .required('Enter a valid Australian mobile number.')
              .test(
                'is-valid-australian-mobile',
                'Enter a valid Australian mobile number.',
                (value) => !value || isValidAustralianMobile(value)
              ),
          otherwise: (schema) => schema.nullable().notRequired(),
        }),

      previousRentWeekly: Yup.number()
        .nullable()
        .transform((value, originalValue) => {
          if (originalValue === '' || originalValue === null) return null
          return isNaN(value) ? null : value
        })
        .when('previousRentalHistory', {
          is: (value: unknown) => toBooleanChoice(value) === true,
          then: (schema) =>
            schema
              .required('Set your previous weekly rent.')
              .moreThan(0, 'Rent must be greater than zero.'),
          otherwise: (schema) => schema.nullable().notRequired(),
        }),

      previousRentDuration: Yup.string()
        .nullable()
        .transform((value) => (value === '' ? null : value))
        .when('previousRentalHistory', {
          is: (value: unknown) => toBooleanChoice(value) === true,
          then: (schema) => schema.required('Pick how long you rented.'),
          otherwise: (schema) => schema.nullable().notRequired(),
        }),
      rentalLedgerUploadLater: Yup.boolean(),
      documents: Yup.array().test(
        'rental-ledger-required',
        "Upload your rental ledger or choose I'll upload later.",
        function (this: Yup.TestContext, documents) {
          if (toBooleanChoice(this.parent?.previousRentalHistory) !== true) {
            return true
          }

          if (this.parent?.rentalLedgerUploadLater === true) {
            return true
          }

          return getUploadedDocumentTypes(documents).has(UPLOAD_SLOT_TYPES.rentalLedger)
        }
      ),
    }),
    onNext: saveNext('rental-history'),
    onPrev: savePrev('rental-history'),
    onAutoSave: saveAuto('rental-history'),
  },
  {
    id: 11,
    key: 'pets-vehicles',
    path: 'pets-vehicles',
    label: 'Pets/Vehicle',
    title: 'Pets & Vehicles',
    showProgressBar: true,
    component: PetsVehiclesStep,
    nextButtonText: 'Continue',
    isSaveEnabled: true,
    schema: Yup.object({
      pets: Yup.array()
        .of(
          Yup.object({
            type: Yup.string().required('Enter the pet type.'),
            breed: Yup.string().required('Enter the breed.'),
          })
        )
        .when('hasPets', {
          is: (val: boolean) => val === true,
          then: (schema) =>
            schema.min(1, 'At least one pet is required.').required('Pets are required.'),
          otherwise: (schema) => schema.nullable().notRequired(),
        }),
      vehicles: Yup.array()
        .of(
          Yup.object({
            //makeModel: Yup.string().required('Vehicle make and model are required.'),
            registrationNumber: Yup.string().required('Enter you rego plate.'),
          })
        )
        .when('hasVehicles', {
          is: (val: boolean) => val === true,
          then: (schema) =>
            schema.min(1, 'At least one vehicle is required.').required('Vehicles are required.'),
          otherwise: (schema) => schema.nullable().notRequired(),
        }),
    }),
    onNext: saveNext('pets-vehicles'),
    onPrev: savePrev('pets-vehicles'),
    onAutoSave: saveAuto('pets-vehicles'),
  },
  {
    id: 12,
    key: 'other-occupants',
    path: 'other-occupants',
    label: 'Other Occupants',
    title: 'Other Occupants',
    showProgressBar: true,
    component: OtherOccupantsStep,
    nextButtonText: 'Continue',
    isSaveEnabled: true,
    schema: Yup.object({
      hasOtherOccupants: Yup.mixed().test(
        'other-occupants-selected',
        'Please confirm whether anyone else will live with you.',
        (value) => toBooleanChoice(value) !== undefined
      ),
      otherOccupants: Yup.array()
        .of(
          Yup.object({
            name: Yup.string().typeError('Enter their name.').required('Enter their name.'),
            age: Yup.number().typeError('Fill in the age.').required('Fill in the age.'),
            relationship: Yup.string()
              .typeError('Fill in the relationship.')
              .required('Fill in the relationship.'),
          })
        )
        .when('hasOtherOccupants', {
          is: (value: unknown) => toBooleanChoice(value) === true,
          then: (schema) =>
            schema.min(1, 'At least one occupant is required.').required('Occupants are required.'),
          otherwise: (schema) => schema.nullable().notRequired(),
        }),
    }),
    onNext: saveNext('other-occupants'),
    onPrev: savePrev('other-occupants'),
    onAutoSave: saveAuto('other-occupants'),
  },
  {
    id: 13,
    key: 'uploads',
    path: 'uploads',
    label: 'Uploads',
    title: 'Documents',
    showProgressBar: true,
    component: UploadsStep,
    nextButtonText: 'Continue',
    isSaveEnabled: true,
    schema: Yup.object({
      identificationType: optionalTrimmedStringSchema().required('ID type is required.'),
      incomeUploadLaterTypes: Yup.array().of(Yup.string()),
      documents: Yup.array()
        .of(
          Yup.object({
            type: Yup.string().required('Document type is required.'),
            filename: Yup.string().required('Document filename is required.'),
          })
        )
        .test(
          'required-upload-slots',
          'Please provide identity documents and upload or save for later both required proof-of-income documents.',
          function (this: Yup.TestContext, documents) {
            const types = getUploadedDocumentTypes(documents)
            const incomeUploadLaterTypes = new Set(this.parent?.incomeUploadLaterTypes ?? [])
            return requiredUploadSlotTypes.every((requiredType) => {
              if (
                requiredType === UPLOAD_SLOT_TYPES.incomeDoc1 ||
                requiredType === UPLOAD_SLOT_TYPES.incomeDoc2
              ) {
                return types.has(requiredType) || incomeUploadLaterTypes.has(requiredType)
              }

              return types.has(requiredType)
            })
          }
        )
        .test(
          'benefit-income-statement-required',
          'Upload your Centrelink income statement or save it for later.',
          function (this: Yup.TestContext, documents) {
            if (!hasBenefitIncomeSource(this.parent?.income)) {
              return true
            }

            const types = getUploadedDocumentTypes(documents)
            const incomeUploadLaterTypes = new Set(this.parent?.incomeUploadLaterTypes ?? [])
            return (
              types.has(UPLOAD_SLOT_TYPES.incomeStatement) ||
              incomeUploadLaterTypes.has(UPLOAD_SLOT_TYPES.incomeStatement)
            )
          }
        )
        .test(
          'visa-upload-required',
          'Upload your visa document or save it for later.',
          function (this: Yup.TestContext, documents) {
            if (!this.parent?.isVisaHolder) {
              return true
            }

            const types = getUploadedDocumentTypes(documents)
            const incomeUploadLaterTypes = new Set(this.parent?.incomeUploadLaterTypes ?? [])
            return (
              types.has(UPLOAD_SLOT_TYPES.visaGrant) ||
              incomeUploadLaterTypes.has(UPLOAD_SLOT_TYPES.visaGrant)
            )
          }
        )
        .test(
          'international-student-upload-required',
          'Please upload both international student documents (CoE and Enrolment Letter).',
          function (this: Yup.TestContext, documents) {
            if (!this.parent?.isInternationalStudent) {
              return true
            }

            const types = getUploadedDocumentTypes(documents)
            return (
              types.has(UPLOAD_SLOT_TYPES.internationalCoe) &&
              types.has(UPLOAD_SLOT_TYPES.enrollmentLetter)
            )
          }
        )
        .test(
          'company-trust-upload-required',
          'Letter of authority is required for Company/Trust applications.',
          function (this: Yup.TestContext, documents) {
            if (this.parent?.applicationType !== 'CompanyTrust') {
              return true
            }

            const types = getUploadedDocumentTypes(documents)
            return types.has(UPLOAD_SLOT_TYPES.letterOfAuthority)
          }
        ),
    }),
    onNext: saveNext('uploads'),
    onPrev: savePrev('uploads'),
    onAutoSave: saveAuto('uploads'),
  },
  {
    id: 14,
    key: 'privacy',
    path: 'privacy',
    label: 'Privacy',
    title: 'Privacy',
    showProgressBar: true,
    component: PrivacyStep,
    nextButtonText: 'Continue to review',
    isSaveEnabled: true,
    schema: Yup.object({
      retentionPreference: Yup.number().required('Retention preference is required.'),
    }),
    condition: (data) => !isInviteWizard(data),
    onNext: privacyStepNext,
    onPrev: savePrev('privacy'),
    onAutoSave: saveAuto('privacy'),
  },
  {
    id: 15,
    key: 'review',
    path: 'review',
    label: 'Review',
    title: 'Review',
    showProgressBar: true,
    component: ReviewStep,
    nextButtonText: 'Finalise your Doora Card',
    isSaveEnabled: false,
    schema: Yup.object({}),
    onNext: reviewStepFinalize,
    onPrev: savePrev('review'),
    onAutoSave: saveAuto('review'),
  }
]
